// FILE: src/hooks/useRazorpayCheckout.js
import { useCallback } from "react";
import { createOrder, reconcilePayment, verifyPayment } from "../api/paymentApi";
import { reconcileOpsBookingPayment, verifyOpsBookingPayment } from "../api/opsBookingApi";

const ENV_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

export default function useRazorpayCheckout() {
  /**
   * openCheckout({
   *   amount,          // INR
   *   user,            // { id?, name, email?, phone? }
   *   notes,           // optional
   *   bookingIds,      // optional legacy [1,2,3]
   *   onVerified,
   *   onClose,
   * })
   */
  const openCheckout = useCallback(
    async ({ amount, user, notes = {}, bookingIds, flow = "legacy", onVerified, onClose }) => {
      if (!window.Razorpay) throw new Error("Razorpay SDK not loaded");
      if (!amount || Number(amount) <= 0) throw new Error("Invalid amount for Razorpay checkout");

      // ✅ 1) Create order
      const order = await createOrder({
        amount: Number(amount),
        userId: typeof user?.id === "number" ? user.id : null,
        userEmail: user?.email || null,
        userPhone: user?.phone || user?.contact || user?.phoneNumber || null,
      });

      const { paymentId, razorpayOrderId, amount: amountPaise, currency, keyId } = order || {};
      if (!razorpayOrderId || !amountPaise) throw new Error("Invalid createOrder response from server");

      const finalKeyId = keyId || ENV_KEY_ID;
      if (!finalKeyId) throw new Error("Razorpay keyId missing (env or backend)");

      const options = {
        key: finalKeyId,
        amount: amountPaise,
        currency: currency || "INR",
        name: "Wayroot Rentals",
        description: "Vehicle booking",
        order_id: razorpayOrderId,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        notes,
        theme: { color: "#0B0E13" },

        handler: async (resp) => {
          const verifyPayload = {
            paymentId,
            order_id: resp.razorpay_order_id,
            payment_id: resp.razorpay_payment_id,
            signature: resp.razorpay_signature,
            userId: typeof user?.id === "number" ? user.id : null,
            userEmail: user?.email || null,
            phoneNumber: user?.phone || user?.contact || user?.phoneNumber || null,
            bookingIds,
          };

          const reconcilePayload = {
            paymentId,
            order_id: resp.razorpay_order_id || razorpayOrderId,
            userId: verifyPayload.userId,
            userEmail: verifyPayload.userEmail,
            phoneNumber: verifyPayload.phoneNumber,
            bookingIds,
          };

          try {
            const verifyRes =
              flow === "ops"
                ? await verifyOpsBookingPayment(verifyPayload)
                : await verifyPayment(verifyPayload);

            if (verifyRes?.success) {
              if (onVerified) onVerified(verifyRes);
              return;
            }

            const reconcileRes =
              flow === "ops"
                ? await reconcileOpsBookingPayment(reconcilePayload)
                : await reconcilePayment(reconcilePayload);

            if (onVerified) onVerified(reconcileRes?.success ? reconcileRes : verifyRes);
          } catch (e) {
            console.error("Payment verification failed:", e);
            if (onVerified) onVerified({ success: false, error: e?.message || "Verification failed" });
          }
        },

        modal: {
          ondismiss: () => {
            if (onClose) onClose();
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    },
    []
  );

  return { openCheckout };
}
